import React, { useRef } from 'react';
import styled from '@emotion/styled';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { GridColDef, GridFilterItem } from '@mui/x-data-grid';
import { usePalette, useTranslation } from 'src/hooks';
import { Button, IconButton, MenuItem, Popover, TextField, Typography, usePopover } from 'src/components/atoms';
import { FlexColumn } from 'src/components/layout';

interface Props {
  filterColumns: GridColDef[];
  applyFilterOnEnter: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  applyFilterOnButtonClick: () => void;
  handleSelectFilterColumn: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilterValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearFilters: () => void;
  filterValue: GridFilterItem;
  title?: string;
}

const StyledGridHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.2rem 0.6rem;
`;

export const DataGridHeader = ({
  filterColumns,
  filterValue,
  title,
  applyFilterOnEnter,
  applyFilterOnButtonClick,
  handleSelectFilterColumn,
  handleFilterValueChange,
  handleClearFilters
}: Props) => {
  const { t } = useTranslation();
  const { background, error } = usePalette();
  const { anchorEl, openPopover, closePopover } = usePopover();
  const filterRef = useRef<HTMLInputElement | null>(null);

  const handleClearFiltersAndClosePopover = () => {
    handleClearFilters();
    closePopover();
  };

  return (
    <>
      <StyledGridHeader>
        <Typography variant='h3'>{title || ''}</Typography>
        <IconButton onClick={!anchorEl ? openPopover : closePopover}>
          <FilterAltIcon />
        </IconButton>
      </StyledGridHeader>
      <Popover anchorEl={anchorEl} onClose={closePopover}>
        <FlexColumn width='20rem' p={2}>
          <Typography>{t('pages.profile.overview_page_grid_header_set_filters_header')}</Typography>
          <TextField
            select
            value={filterValue?.columnField || ''}
            onChange={handleSelectFilterColumn}
            variant='standard'
            placeholder={t('pages.profile.overview_page_grid_header_set_filters_field_column')}
          >
            {filterColumns.map(col => (
              <MenuItem key={col.field} value={col.field}>
                {col.headerName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            value={filterValue.value}
            inputRef={filterRef}
            variant='standard'
            placeholder={t('pages.profile.overview_page_grid_header_set_filters_field_value')}
            onChange={handleFilterValueChange}
            onKeyDown={applyFilterOnEnter}
            size='small'
          />
          <Button onClick={applyFilterOnButtonClick} sx={{ mt: 2, backgroundColor: background.default }}>
            {t('defaults.button.set_filters')}
          </Button>
          <Button onClick={handleClearFiltersAndClosePopover} sx={{ mt: 2, backgroundColor: error.main }}>
            {t('defaults.button.clear_filters')}
          </Button>
        </FlexColumn>
      </Popover>
    </>
  );
};
